// eslint-disable-next-line @typescript-eslint/ban-ts-comment -- pi-lens/Deno resolver false positives on Expo route imports and downstream JSX typing in this file; repo-native toolchain resolves these correctly
// @ts-nocheck
/* eslint-disable react-hooks/set-state-in-effect -- Auth transitions, quota warnings and reduced-motion reveal synchronize local presentation state. */
/**
 * Unified Analysis Screen - Modern UI + Full Data Flow
 *
 * Main home screen combining:
 * - Letter throwing animation (letterZoom variant, 1.25x speed)
 * - Full data flows (quota, trial prompts, analytics)
 * - Reduced motion accessibility fallback (WCAG 2.1 AA)
 *
 * @see .claude/plans/swift-wibbling-map.md - Agent validation results
 */

// @ts-expect-error -- pi-lens Deno resolver false positive; Expo app resolves react normally
import React, { useState, useEffect, useMemo, useCallback } from "react";
// @ts-expect-error -- pi-lens Deno resolver false positive; Expo app resolves react-native normally
import { View, ScrollView, Platform, StyleSheet } from "react-native";
// @ts-expect-error -- pi-lens Deno resolver false positive; Expo app resolves react-native-keyboard-controller normally
import { KeyboardAvoidingView } from "react-native-keyboard-controller";
// @ts-expect-error -- pi-lens Deno resolver false positive; Expo app resolves expo-router normally
import { useLocalSearchParams, useRouter } from "expo-router";
// @ts-expect-error -- pi-lens Deno resolver false positive; Expo app resolves react-native-reanimated normally
import Animated, { FadeIn, FadeOut, SlideInUp } from "react-native-reanimated";
// @ts-expect-error -- pi-lens Deno resolver false positive for workspace package resolution
import { TierBody } from "@dramadetector/ui";
import { useTranslation } from "@dramadetector/shared";

// Hooks
import { useMessageInput } from "../../hooks/useMessageInput";
import { useTrialPrompts } from "../../hooks/useTrialPrompts";
import { useMobileWebKeyboard } from "../../hooks/useMobileWebKeyboard";
import { useResponsiveScreenPadding } from "../../components/layout/TabScreenWrapper";
import { useKeyboardVisibility } from "../../hooks/useKeyboardVisibility";
import { useQuotaData } from "../../hooks/useQuotaData";
import { useTurnstile } from "../../hooks/quota/useTurnstile";
import { useQuotaModals } from "../../hooks/quota/useQuotaModals";
import { useTier } from "../../hooks/useTier";
import {
	useQuotaWarnings,
	determineWarningLevel,
} from "../../hooks/useQuotaWarnings";
import { useAnalyze } from "../../features/analysis/hooks";
import { useAnalyzeHandler } from "../../hooks/useAnalyzeHandler";
import { useAuth } from "../../contexts/AuthContext";
import { useReducedMotionWithLoading } from "../../hooks/useReducedMotionWithLoading";
import { useTheatricalMode } from "../../hooks/useTheatricalMode";
import {
	useL3AccordionsFlag,
	useGenerativeResultsFlag,
	useTurnstileEnabledFlag,
} from "../../hooks/useFeatureFlags";
import { useScreenAnalytics } from "../../hooks/useScreenAnalytics";
import { useHomeExperiencePolicy } from "../../hooks/useHomeExperiencePolicy";
import { useResultsExperiencePolicy } from "../../hooks/useResultsExperiencePolicy";
import { useVisualViewport } from "../../hooks/useVisualViewport"; // Phase 1: iOS Safari viewport fix
import { isMobileWebBrowser } from "../../utils/platform";
import { captureEvent } from "../../lib/analytics";
import { scrubString } from "../../lib/analytics/scrubbers";
import { trackEvent } from "@/lib/analytics/events";
import { requiresAnonymousTurnstileVerification } from "../../lib/turnstile/anonymousTurnstileGate";
import type { QuotaTier } from "../../components/trial/QuotaExhaustedModal";
import type { RevisionTrackingConfig } from "../../components/modern-results/inline-annotations/InlineMessageDisplay";

// Components
import { StickyHeader } from "../../components/StickyHeader";
import { HeroSection } from "../../components/screens/home/HeroSection";
import { AnalysisSection } from "../../components/screens/home/AnalysisSection";
import { QuotaWarningsSection } from "../../components/screens/home/QuotaWarningsSection";
import { ViewingModeSelector } from "../../components/theatrical/ViewingModeSelector";
import { CrisisBanner } from "../../components/safety/CrisisBanner";
import {
	ModernResultsPreview,
	ModernResultsFull,
} from "../../components/modern-results";
import { DDGenerativeResults } from "../../components/modern-results/DDGenerativeResults";
import { ErrorBoundary } from "../../components/ErrorBoundary";
import { OfflineBanner } from "../../components/OfflineBanner";

export default function AnalyzeUnifiedScreen() {
	void React;
	// eslint-disable-next-line local-rules/no-translation-namespace-mismatch -- home screen copy lives in the home namespace for this route
	const { t } = useTranslation("home");
	const router = useRouter();
	const params = useLocalSearchParams<{
		message?: string;
		error?: string;
		resetForm?: string;
	}>();

	// Phase 1: iOS Safari viewport fix - sets --viewport-height CSS variable
	useVisualViewport();

	// Keyboard handling
	const { isKeyboardVisible } = useKeyboardVisibility();

	// MOBILE WEB KEYBOARD FIX: Detect mobile web for fixed header
	const isMobileWeb = Platform.OS === "web" && isMobileWebBrowser();

	// Responsive screen padding
	const { bottomPadding } = useResponsiveScreenPadding();

	// Mobile web keyboard scroll (disabled when compact controls visible)
	useMobileWebKeyboard({
		enabled: !(Platform.OS === "web" && isKeyboardVisible),
		shouldSkipScroll: () =>
			Platform.OS === "web" &&
			document
				.querySelector('[data-analysis-section="true"]')
				?.querySelector('[data-compact-controls="true"]') !== null,
	});

	// Auth.
	// Phase 2 (NocoDB #3869 close-out): use AuthContext's canonical `isAnonymous`
	// and `isRealUser` derived booleans instead of re-deriving from `!accessToken`.
	// Anon BA users HAVE an accessToken, so the old pattern misclassified them and
	// hid the AnonymousQuotaCounter (original homepage-quota regression).
	const {
		accessToken,
		user,
		isAnonymous,
		isRealUser,
		loading: authLoading,
	} = useAuth();

	// Reduced motion preference (WCAG 2.1 AA)
	const prefersReducedMotion = useReducedMotionWithLoading() ?? false;

	// DRAMA-WEB-28: clearance token state
	const [clearanceToken, setClearanceToken] = useState<string | null>(null);

	// DRAMA-WEB-28: clear stale anonymous clearance token on login
	// eslint-disable-next-line local-rules/no-race-conditions -- isolated auth-transition cleanup effect; no competing writer effect for clearanceToken semantics
	useEffect(() => {
		if (accessToken) setClearanceToken(null);
	}, [accessToken]);

	// Analysis hook
	const {
		analyze,
		isPending,
		data,
		error: apiError,
		reset,
	} = useAnalyze({ userId: user?.id, onClearanceToken: setClearanceToken });

	// Quota (consolidated hook)
	const {
		quotaData,
		tier: quotaTier,
		characterLimit,
		isEntitlementsLoading,
	} = useQuotaData();

	// Turnstile CAPTCHA (anonymous users)
	const turnstileEnabled = useTurnstileEnabledFlag();
	const {
		turnstileToken,
		setTurnstileToken,
		turnstileError,
		setTurnstileError,
		handleTurnstileSuccess,
		handleTurnstileError,
		turnstileRef,
		requestReset,
	} = useTurnstile({
		isAnonymous,
		hasAnalysisData: !!data?.data,
	});

	// Modal state
	const { showQuotaModal, showIpLimitModal } = useQuotaModals({
		apiError,
		quotaData,
	});

	// Tier and feature flags
	const {
		tier: entitlementsTier,
		tierLevel,
		visualLayer,
		source,
		isPremium,
	} = useTier();
	const l3AccordionsEnabled = useL3AccordionsFlag();
	const generativeResultsEnabled = useGenerativeResultsFlag();

	// Theatrical mode
	const { variant: theatricalVariant, setMode: setTheatricalMode } =
		useTheatricalMode();

	// Input validation
	const {
		message,
		context,
		maxLength,
		error: validationErrorObj,
		isValid,
		setMessage,
		setContext,
		validateMessage,
	} = useMessageInput({
		maxLength: characterLimit,
	});

	const validationError = useMemo(() => {
		if (!validationErrorObj) return null;
		return t(
			`message_input.validation.${validationErrorObj.code}`,
			validationErrorObj.values,
		);
	}, [validationErrorObj, t]);

	// Handle form reset from "Analyze Another" navigation (consume-once pattern)
	// eslint-disable-next-line local-rules/no-race-conditions -- one-shot route-param consumption effect; intentional local state reset followed by router replace
	useEffect(() => {
		if (params.resetForm === "true") {
			setMessage("");
			setContext("family");
			// Consume param to prevent repeated clearing on refresh/back
			router.replace("/(tabs)");
		}
	}, [params.resetForm, setMessage, setContext, router]);

	// Height tracking for FAB docking
	// All users: 168px (6 lines)
	const [_inputHeight, setInputHeight] = useState(168);

	// Trial prompts (hooks preserved, UI disabled)
	const { analysisCount, isLoaded: isTrialStateLoaded } = useTrialPrompts({
		isAnonymous,
		analysisData: data,
		quotaUsed: quotaData?.used ?? 0,
		quotaLimit: quotaData?.limit ?? 10,
	});

	// Quota warnings
	const { shouldShowWarning, markWarningShown } = useQuotaWarnings();
	const [activeWarning, setActiveWarning] = useState<{
		level: "50_percent" | "80_percent" | "90_percent";
		type: "banner" | "toast" | "modal";
	} | null>(null);

	// Current tier — prefer quotaData.tier (most up-to-date), fall back to entitlements tier.
	// Phase 2: anon BA users have an accessToken but tier === "anonymous". Use `isAnonymous`
	// (the already-derived boolean above) so anon BA users aren't mistakenly routed to the
	// authed tier path.
	const currentTier: QuotaTier = (() => {
		if (isAnonymous) return "anonymous";
		if (quotaData?.tier) return quotaData.tier as QuotaTier;
		if (!quotaTier) return "free";
		return quotaTier as QuotaTier;
	})();

	const homePolicy = useHomeExperiencePolicy({
		accessToken,
		userId: user?.id ?? null,
		isAnonymous,
		isRealUser,
		authLoading,
		tier: entitlementsTier,
		tierLevel,
		visualLayer,
		source,
		isPremium,
		isEntitlementsLoading,
		analysisCount,
		isTrialStateLoaded,
		hasAnalysis: Boolean(data?.data),
		errorCode: apiError?.code ?? null,
	});

	const resultsPolicy = useResultsExperiencePolicy({
		surface: "results",
		accessToken,
		userId: user?.id ?? null,
		isAnonymous,
		isRealUser,
		authLoading,
		tier: entitlementsTier,
		tierLevel,
		visualLayer,
		source,
		isPremium,
		isEntitlementsLoading,
		analysisCount,
		isTrialStateLoaded,
		analysis: data?.data ?? null,
		hasRemoteConfig: generativeResultsEnabled,
		hasSharedLinkPayload: false,
		errorCode: apiError?.code ?? null,
	});

	const homeInputPlaceholder =
		homePolicy.inputPlaceholderKey === "message_before_send"
			? t("home.input_placeholder_message_before_send", {
					defaultValue: "Paste a message you're about to send...",
				})
			: t("home.input_placeholder");
	const experienceVisibility = !isKeyboardVisible
		? homePolicy.experienceVisibility
		: homePolicy.experienceVisibility === "selector"
			? "compact"
			: "hidden";

	// Reduced motion results state
	const [showReducedMotionResults, setShowReducedMotionResults] =
		useState(false);
	const [lastShownAnalysisId, setLastShownAnalysisId] = useState<string | null>(
		null,
	);

	// Analytics tracking
	useScreenAnalytics({
		prefersReducedMotion,
		currentTier,
		showReducedMotionResults,
		analysisData: data?.data ?? undefined,
	});

	// Revision tracking config
	const trackingConfig = useMemo((): RevisionTrackingConfig | undefined => {
		if (!accessToken || !data?.data) return undefined;
		const analysisData = data.data;
		return {
			analysisId: analysisData.id,
			originalScore: analysisData.score ?? 0,
			originalSummary: analysisData.summary,
			modelId: analysisData.metadata?.model,
			enabled: true,
			// M4.2: Scrub error_message to prevent PII leakage to PostHog
			onTrackingError: (error: Error, ctx: string) =>
				captureEvent("Revision_Tracking_Error", {
					context: ctx,
					error_message: scrubString(error.message),
					tier: currentTier,
					analysis_id: analysisData.id,
				}),
		};
	}, [accessToken, data, currentTier]);

	// Analyze handler
	const { handleAnalyze } = useAnalyzeHandler({
		message,
		context,
		validateMessage,
		// Phase 2 (NocoDB #3869 close-out): pass `isRealUser` so useAnalyzeHandler's
		// internal `const isAnonymous = !isAuthenticated` doesn't misclassify anon
		// BA users as authed (Greptile P2 on #1009). Anon BA users have an
		// accessToken but are semantically anonymous — they still need Turnstile.
		isAuthenticated: isRealUser,
		quotaData,
		prefersReducedMotion,
		theatricalVariant,
		turnstileToken,
		clearanceToken,
		requestTurnstileReset: requestReset,
		setTurnstileError,
		analyze: analyze as (
			text: string,
			context: string,
			token?: string,
			clearanceToken?: string,
		) => string,
	});

	// Effects
	// eslint-disable-next-line local-rules/no-race-conditions -- route-param hydration effect; independent from reset/new-analysis flows and intentionally seeds input state
	useEffect(() => {
		if (params.message) setMessage(params.message);
	}, [params.message, setMessage]);

	// eslint-disable-next-line local-rules/no-race-conditions -- quota warning derivation effect updates presentation state only and does not race with input/auth effects
	useEffect(() => {
		if (quotaData && quotaData.tier !== "anonymous") {
			const warning = determineWarningLevel(
				quotaData.usagePercent,
				shouldShowWarning,
			);
			if (warning) setActiveWarning(warning);
		}
	}, [quotaData, shouldShowWarning]);

	// eslint-disable-next-line local-rules/no-race-conditions -- reduced-motion reveal effect only gates local presentation of fetched results and is independent from other effects
	useEffect(() => {
		if (
			prefersReducedMotion &&
			data?.data &&
			!isPending &&
			data.data.id !== lastShownAnalysisId
		) {
			setShowReducedMotionResults(true);
			setLastShownAnalysisId(data.data.id);
		}
	}, [prefersReducedMotion, data, isPending, lastShownAnalysisId]);

	// Handlers
	const platformType = Platform.OS as "ios" | "android" | "web";

	const handleNewAnalysis = useCallback(() => {
		captureEvent("unified_new_analysis_clicked", {
			tier: currentTier,
			platform: platformType,
		});
		reset();
		setMessage("");
		setShowReducedMotionResults(false);
		setLastShownAnalysisId(null); // Clear analysis ID to prevent re-showing old results
	}, [reset, setMessage, currentTier, platformType]);

	const handleUpgrade = useCallback(() => {
		captureEvent("quota_upgrade_clicked", {
			current_tier: currentTier,
			source: "unified_screen",
			platform: platformType,
		});
		router.push("/(tabs)/subscription");
	}, [currentTier, router, platformType]);

	const handleSignupClick = useCallback(() => {
		captureEvent("trial_signup_intent", {
			source: "unified_screen",
			analyses_completed: analysisCount,
			platform: platformType,
		});
		router.push("/(auth)/signup");
	}, [analysisCount, router, platformType]);

	const handlePaste = useCallback(
		(text: string) =>
			setTimeout(
				() =>
					trackEvent("Analysis_ClipboardPaste", {
						text_length: text.length,
						platform: platformType,
						user_agent:
							Platform.OS === "web"
								? navigator?.userAgent || "unknown"
								: `${Platform.OS}-native`,
					}),
				0,
			),
		[platformType],
	);

	// Computed
	const isQuotaExceeded =
		quotaData &&
		quotaData.remaining <= 0 &&
		(quotaData.tier === "free" || quotaData.tier === "anonymous");
	// DRAMA-WEB-28: clearanceToken is an alternative to turnstileToken
	// Note: isTheatricalModeLoading intentionally excluded — theatrical mode only affects
	// post-submit animation and defaults to 'theatrical' while loading, so it should
	// never block the submit button.
	const isAnalyzeDisabled =
		!isValid ||
		isPending ||
		requiresAnonymousTurnstileVerification({
			isAnonymous,
			turnstileEnabled,
			turnstileToken,
			clearanceToken,
		}) ||
		!!isQuotaExceeded;

	return (
		<ErrorBoundary componentName="AnalyzeUnifiedScreen">
			<View style={styles.container}>
				<View style={[StyleSheet.absoluteFill, styles.background]} />
				<StickyHeader />
				<OfflineBanner />

				{!showReducedMotionResults && (
					<Animated.View
						entering={FadeIn.duration(300)}
						exiting={FadeOut.duration(200)}
						style={styles.flex}
					>
						<KeyboardAvoidingView
							behavior={Platform.OS === "web" ? undefined : "padding"}
							enabled={Platform.OS !== "web"}
							style={styles.flex}
						>
							<ScrollView
								style={styles.flex}
								contentContainerStyle={[
									styles.inputContent,
									isKeyboardVisible && styles.keyboardVisibleInputContent,
									isMobileWeb && styles.mobileWebInputContent,
								]}
								keyboardShouldPersistTaps="handled"
							>
								<HeroSection isKeyboardVisible={isKeyboardVisible} />

								{/* Progressive disclosure: Hide quota banner when keyboard visible */}
								{!isKeyboardVisible && (
									<QuotaWarningsSection
										activeWarning={
											activeWarning?.type === "banner" ? activeWarning : null
										}
										quotaData={
											quotaData
												? {
														dailyAnalysesUsed: quotaData.used,
														dailyAnalysesLimit: quotaData.limit,
														formattedResetTime: quotaData.formattedResetTime,
													}
												: null
										}
										isAuthenticated={isRealUser}
										currentTier={currentTier}
										onCloseWarning={() => {
											if (activeWarning) {
												markWarningShown(activeWarning.level, "banner");
												setActiveWarning(null);
											}
										}}
										onNavigateToSubscription={handleUpgrade}
									/>
								)}

								{apiError && (
									<Animated.View
										entering={FadeIn.duration(200)}
										style={styles.errorBanner}
									>
										<View style={styles.errorBannerContent}>
											<TierBody size="sm" color="text-red-600">
												{t("home.errors.analysis_failed", {
													defaultValue: "Analysis Failed",
												})}
											</TierBody>
											<TierBody size="xs" color="text-red-500">
												{apiError.code === "NETWORK_ERROR"
													? t("home.errors.network_error", {
															defaultValue: apiError.message,
														})
													: apiError.message ||
														t("home.errors.unexpected_error")}
											</TierBody>
										</View>
									</Animated.View>
								)}

								{/* Progressive disclosure: Hide viewing mode selector when keyboard visible */}
								{experienceVisibility === "selector" && (
									<View style={styles.viewingModeSection}>
										<TierBody size="sm" style={styles.viewingModeLabel}>
											{t("home.choose_experience", {
												defaultValue: "Choose your experience:",
											})}
										</TierBody>
										<ViewingModeSelector
											selectedMode={theatricalVariant}
											onSelectMode={setTheatricalMode}
											compact={Platform.OS === "web"}
										/>
									</View>
								)}

								<AnalysisSection
									message={message}
									context={context}
									validationError={validationError}
									isValid={isValid}
									maxLength={maxLength}
									turnstileToken={turnstileToken}
									turnstileError={turnstileError}
									clearanceToken={clearanceToken}
									isAnonymous={isAnonymous}
									isPending={isPending}
									isAnalyzeDisabled={isAnalyzeDisabled}
									quotaRemaining={quotaData?.remaining ?? 0}
									quotaLimit={quotaData?.limit ?? 0}
									formattedResetTime={quotaData?.formattedResetTime ?? ""}
									isQuotaModalVisible={showQuotaModal || showIpLimitModal}
									isKeyboardVisible={isKeyboardVisible}
									experienceVisibility={experienceVisibility}
									experienceMode={theatricalVariant}
									onExperienceModeChange={setTheatricalMode}
									placeholderText={homeInputPlaceholder}
									onMessageChange={setMessage}
									onInputHeightChange={setInputHeight}
									onContextChange={setContext}
									onAnalyze={handleAnalyze}
									onPaste={handlePaste}
									onTurnstileSuccess={handleTurnstileSuccess}
									onTurnstileError={handleTurnstileError}
									onTurnstileExpire={() => setTurnstileToken(null)}
									turnstileRef={turnstileRef}
								/>
							</ScrollView>
						</KeyboardAvoidingView>
					</Animated.View>
				)}

				{showReducedMotionResults && data?.data && !isPending && (
					<Animated.View
						entering={SlideInUp.springify().damping(15)}
						style={styles.flex}
					>
						<ScrollView
							style={styles.flex}
							contentContainerStyle={[
								styles.resultsContent,
								{ paddingBottom: bottomPadding },
							]}
						>
							{data.data.safety?.showResources && (
								<CrisisBanner
									crisisDetected={data.data.safety.crisisDetected}
									severity={data.data.safety.severity}
									moderationEventId={data.data.safety.moderationEventId}
									screen="results"
									testID="crisis-banner-unified"
								/>
							)}
							{currentTier === "anonymous" || currentTier === "free" ? (
								<ModernResultsPreview
									analysis={data.data}
									message={message}
									tier={currentTier}
									onNewAnalysis={handleNewAnalysis}
									onUpgrade={handleUpgrade}
									t={
										t as (
											key: string,
											options?: Record<string, unknown>,
										) => string
									}
									shareTreatment={resultsPolicy.shareTreatment}
									primaryActionTreatment={resultsPolicy.primaryActionTreatment}
									analyticsSurface="home_inline"
									moduleOrder={resultsPolicy.moduleOrder}
									showLikelyImpact={resultsPolicy.showLikelyImpact}
									showPrimaryRewriteCard={resultsPolicy.showPrimaryRewriteCard}
								/>
							) : generativeResultsEnabled ? (
								<DDGenerativeResults
									analysis={data.data}
									message={message}
									context={context}
									tierLevel={tierLevel}
									tier={currentTier}
									t={
										t as (
											key: string,
											options?: Record<string, unknown>,
										) => string
									}
									onNewAnalysis={handleNewAnalysis}
									onUpgrade={handleUpgrade}
									l3AccordionsEnabled={l3AccordionsEnabled}
									tracking={trackingConfig}
									analyticsSurface="home_inline"
								/>
							) : (
								<ModernResultsFull
									analysis={data.data}
									message={message}
									context={context}
									tierLevel={tierLevel}
									tier={currentTier}
									onNewAnalysis={handleNewAnalysis}
									onUpgrade={handleUpgrade}
									l3AccordionsEnabled={l3AccordionsEnabled}
									t={
										t as (
											key: string,
											options?: Record<string, unknown>,
										) => string
									}
									tracking={trackingConfig}
									shareTreatment={resultsPolicy.shareTreatment}
									primaryActionTreatment={resultsPolicy.primaryActionTreatment}
									analyticsSurface="home_inline"
									moduleOrder={resultsPolicy.moduleOrder}
									showLikelyImpact={resultsPolicy.showLikelyImpact}
									showPrimaryRewriteCard={resultsPolicy.showPrimaryRewriteCard}
								/>
							)}
						</ScrollView>
					</Animated.View>
				)}

				<QuotaWarningsSection
					activeWarning={
						activeWarning?.type !== "banner" ? activeWarning : null
					}
					quotaData={
						quotaData
							? {
									dailyAnalysesUsed: quotaData.used,
									dailyAnalysesLimit: quotaData.limit,
									formattedResetTime: quotaData.formattedResetTime,
								}
							: null
					}
					isAuthenticated={isRealUser}
					currentTier={currentTier}
					onCloseWarning={() => {
						if (activeWarning) {
							markWarningShown(activeWarning.level, activeWarning.type);
							setActiveWarning(null);
						}
					}}
					onNavigateToSubscription={
						isRealUser ? handleUpgrade : handleSignupClick
					}
				/>
			</View>
		</ErrorBoundary>
	);
}

const styles = StyleSheet.create({
	container: { flex: 1 },
	background: { backgroundColor: "#1a1a2e" },
	flex: { flex: 1 },
	inputContent: {
		padding: Platform.OS === "web" ? 16 : 24,
		paddingBottom: 12,
		paddingTop: Platform.OS === "web" ? 16 : 24,
	},
	resultsContent: {
		padding: 24,
		// Note: paddingBottom is applied inline via useResponsiveScreenPadding()
	},
	viewingModeSection: {
		marginBottom: Platform.OS === "web" ? 12 : 20,
		alignItems: "center",
		gap: 8,
	},
	viewingModeLabel: {
		textAlign: "center",
		color: "#FFFFFF",
		fontWeight: "600",
		fontSize: 13,
	},
	errorBanner: {
		marginBottom: 12,
		borderRadius: 12,
		backgroundColor: "rgba(254, 226, 226, 0.9)",
		borderWidth: 1,
		borderColor: "rgba(239, 68, 68, 0.3)",
	},
	errorBannerContent: { padding: 12, gap: 4 },
	keyboardVisibleInputContent: { paddingBottom: 0 },
	mobileWebInputContent: { paddingTop: 56 },
});
